import { GidMatrix, IfcData } from "@/services/admin-services/models";
import { UseCase } from "@/services/global-services/models";
import { useEffect, useMemo, useReducer } from "react";
import DataTableCaption from "./components/CaptionsContainer";
import DataTableContainer from "./components/DataTableContainer";
import FootnotesContainer from "./components/FootnotesContainer";
import { DataTableContext } from "./context/DataTableContext";
import {
    DataTableReducer,
    ensureEmptyRow,
    initializeDataSource,
    setActiveDisciplines,
    setColumnFilterFunction,
    setRowFilterFunction,
    setUseCaseFilterFunction,
    updateConfig,
} from "./context/DataTableReducer";
import { initialState } from "./context/DataTableState";
import { DataTableCell, DataTableColumnDefinition, DataTableConfig, DataTableData, DataTableRow } from "./Models";

const verifyDataTableContent = (
    rows: Array<DataTableRow>,
    columns: Array<DataTableColumnDefinition>
): Array<DataTableRow> => {
    if (rows.length === 0) return rows;

    const verifiedData: Array<DataTableRow> = [];

    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        const softwareCellIdPrefix = `${row.id}_software_`;
        const classificationCellIdPrefix = `${row.id}_classification_`;
        const verifiedRow: DataTableRow = { ...row, cells: [] };

        // Skip if row is empty
        if (row.cells.every((x) => !x.value)) continue;

        for (let j = 0; j < row.cells.length; j++) {
            const cell = row.cells[j];

            if (cell.id.startsWith(softwareCellIdPrefix) && !cell.metadata?.softwareId) {
                cell.metadata = { ...cell.metadata, softwareId: cell.id.replace(softwareCellIdPrefix, "") };
            }

            if (cell.id.startsWith(classificationCellIdPrefix) && !cell.metadata?.classificationId) {
                cell.metadata = { ...cell.metadata, classificationId: cell.id.replace(classificationCellIdPrefix, "") };
            }
        }

        for (let j = 0; j < columns.length; j++) {
            const column = columns[j];
            let cell: DataTableCell;

            if (column.metadata?.softwareId) {
                const softwareCell = row.cells.find((x) => x.metadata?.softwareId === column.metadata?.softwareId);
                if (softwareCell) {
                    cell = softwareCell;
                } else {
                    if (j < row.cells.length && !row.cells[j].metadata?.softwareId) {
                        cell = row.cells[j];
                    } else {
                        cell = {
                            id: `${row.id}_software_${column.metadata?.softwareId}`,
                            value: "",
                            footnotes: [],
                            metadata: {
                                softwareId: column.metadata?.softwareId,
                            },
                        };
                    }
                }
            } else if (column.metadata?.classificationId) {
                const classificationCell = row.cells.find(
                    (x) => x.metadata?.classificationId === column.metadata?.classificationId
                );
                if (classificationCell) {
                    cell = classificationCell;
                } else {
                    cell = {
                        id: `${row.id}_classification_${column.metadata?.classificationId}`,
                        value: "",
                        footnotes: [],
                        metadata: {
                            classificationId: column.metadata?.classificationId,
                        },
                    };
                }
            } else {
                cell = row.cells[j];
            }

            verifiedRow.cells.push(cell);
        }

        verifiedData.push(verifiedRow);
    }

    return verifiedData;
};

type DataTableProps = {
    config: DataTableConfig;
    data: DataTableData;
    dataSource?: GidMatrix | IfcData;
    dataSourceType?: "gid" | "ifc";
    activeDisciplines?: Array<string>;
    readOnly?: boolean;
    onChange?: (data: DataTableData) => void;
    onFilterColumn?: (column: DataTableColumnDefinition) => boolean;
    onFilterRow?: (row: DataTableRow) => boolean;
    onFilterUseCase?: (useCase: UseCase) => boolean;
};

const DataTable = ({
    config,
    data,
    dataSource = undefined,
    dataSourceType = undefined,
    activeDisciplines = [],
    readOnly = false,
    onChange = () => undefined,
    onFilterColumn = undefined,
    onFilterRow = undefined,
    onFilterUseCase = undefined,
}: DataTableProps) => {
    const content = useMemo(() => verifyDataTableContent(data.rows, config.columns), [data.rows, config.columns]);
    const [state, dispatch] = useReducer(DataTableReducer, {
        ...initialState,
        title: config.title,
        columns: config.columns,
        rows: content,
        footnotes: data.footnotes,
        caption: data.caption || config.caption,
        showGroupHeaderRows: Boolean(config.showGroupHeaderRows),
        hasPropertyLevel: Boolean(config.hasPropertyLevel),
        useCases: config.useCases,
        disciplines: config.disciplines,
        activeDisciplines,
        dataSource,
        dataSourceType,
        readOnly,
        onChange,
    });

    useEffect(() => {
        dispatch(ensureEmptyRow());
    }, []);

    useEffect(() => {
        dispatch(updateConfig(config));
    }, [config]);

    useEffect(() => {
        if (!dataSource) return;
        dispatch(initializeDataSource());
    }, [dataSource]);

    useEffect(() => {
        if (!onFilterColumn) return;
        dispatch(setColumnFilterFunction(onFilterColumn!));
    }, [onFilterColumn]);

    useEffect(() => {
        if (!onFilterRow) return;
        dispatch(setRowFilterFunction(onFilterRow!));
    }, [onFilterRow]);

    useEffect(() => {
        if (!onFilterUseCase) return;
        dispatch(setUseCaseFilterFunction(onFilterUseCase!));
    }, [onFilterUseCase]);

    useEffect(() => {
        dispatch(setActiveDisciplines(!activeDisciplines ? [] : activeDisciplines));
    }, [activeDisciplines]);

    return (
        <DataTableContext.Provider value={{ state, dispatch }}>
            <DataTableContainer />
            <DataTableCaption />
            <FootnotesContainer />
        </DataTableContext.Provider>
    );
};

export default DataTable;
